// 🎨 src/theme.js
// Thème global - Design Premium 2026
// Lisibilité renforcée, sans refonte visuelle

import { alpha, createTheme } from '@mui/material/styles';

const baseTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#2C5545',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#C06E52',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#FAFAF8',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1B2420',
      secondary: '#5A5A40',
    },
    divider: 'rgba(27, 36, 32, 0.08)',
  },
  shape: {
    borderRadius: 16,
  },
  shadows: [
    'none',
    '0px 2px 4px rgba(0,0,0,0.2)',
    '0px 4px 8px rgba(0,0,0,0.3)',
    '0px 8px 16px rgba(0,0,0,0.4)',
    ...Array(21).fill('none'),
  ],
});

const theme = createTheme(baseTheme, {
  typography: {
    fontFamily: '"Outfit", "Inter", "Roboto", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 800,
      letterSpacing: '-0.02em',
      lineHeight: 1.1,
      [baseTheme.breakpoints.down('sm')]: {
        fontSize: '2.1rem',
        lineHeight: 1.15,
      },
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 700,
      letterSpacing: '-0.01em',
      lineHeight: 1.15,
      [baseTheme.breakpoints.down('sm')]: {
        fontSize: '1.7rem',
      },
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 700,
      lineHeight: 1.2,
      [baseTheme.breakpoints.down('sm')]: {
        fontSize: '1.5rem',
      },
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.25,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h6: {
      fontSize: '1.1rem',
      fontWeight: 600,
      lineHeight: 1.35,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
      color: baseTheme.palette.text.secondary,
    },
    subtitle2: {
      fontSize: '0.9375rem',
      fontWeight: 600,
      lineHeight: 1.45,
      color: baseTheme.palette.text.secondary,
      [baseTheme.breakpoints.down('sm')]: {
        fontSize: '0.97rem',
      },
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.65,
      color: baseTheme.palette.text.primary,
      [baseTheme.breakpoints.down('sm')]: {
        fontSize: '1.02rem',
      },
    },
    body2: {
      fontSize: '0.9375rem',
      fontWeight: 400,
      lineHeight: 1.6,
      color: baseTheme.palette.text.secondary,
      [baseTheme.breakpoints.down('sm')]: {
        fontSize: '0.95rem',
      },
    },
    caption: {
      fontSize: '0.82rem',
      fontWeight: 500,
      lineHeight: 1.45,
      color: baseTheme.palette.text.secondary,
      [baseTheme.breakpoints.down('sm')]: {
        fontSize: '0.85rem',
      },
    },
    overline: {
      fontSize: '0.78rem',
      fontWeight: 700,
      letterSpacing: '0.08em',
      lineHeight: 1.5,
      color: baseTheme.palette.text.secondary,
      [baseTheme.breakpoints.down('sm')]: {
        fontSize: '0.8rem',
        letterSpacing: '0.06em',
      },
    },
    button: {
      textTransform: 'none',
      fontWeight: 700,
      fontSize: '0.95rem',
      lineHeight: 1.2,
      letterSpacing: '0.01em',
    },
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 10,
          minHeight: 42,
          padding: '10px 24px',
          textTransform: 'none',
          boxShadow: 'none',
          fontWeight: 700,
          lineHeight: 1.2,
          transition: 'all 0.3s ease-in-out',
          [theme.breakpoints.down('sm')]: {
            minHeight: 46,
            padding: '12px 18px',
            fontSize: '0.95rem',
          },
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
          },
          '&:active': {
            transform: 'translateY(0)',
            boxShadow: 'none',
          },
        }),
        containedPrimary: ({ theme }) => ({
          background: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          '&:hover': {
            background: alpha(theme.palette.primary.main, 0.92),
          },
        }),
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'medium',
      },
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiFormHelperText-root': {
            marginLeft: 2,
            marginRight: 2,
          },
          [theme.breakpoints.down('sm')]: {
            '& .MuiInputBase-root': {
              fontSize: '1rem',
            },
          },
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 10,
          backgroundColor: alpha(theme.palette.background.default, 0.9),
          color: theme.palette.text.primary,
          transition: 'all 0.25s ease-in-out',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha(theme.palette.text.primary, 0.12),
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha(theme.palette.text.primary, 0.28),
          },
          '&.Mui-focused': {
            backgroundColor: theme.palette.background.paper,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
            borderWidth: 2,
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderWidth: 1.5,
          },
          [theme.breakpoints.down('sm')]: {
            minHeight: 48,
          },
        }),
        input: ({ theme }) => ({
          fontSize: '0.98rem',
          lineHeight: 1.5,
          padding: '13px 14px',
          color: theme.palette.text.primary,
          '&::placeholder': {
            color: alpha(theme.palette.text.secondary, 0.82),
            opacity: 1,
          },
          [theme.breakpoints.down('sm')]: {
            fontSize: '1rem',
            padding: '14px 14px',
          },
        }),
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.secondary,
          fontSize: '0.95rem',
          fontWeight: 600,
          lineHeight: 1.4,
          '&.Mui-focused': {
            color: theme.palette.primary.main,
          },
          '&.MuiInputLabel-shrink': {
            color: theme.palette.text.secondary,
          },
          [theme.breakpoints.down('sm')]: {
            fontSize: '0.97rem',
          },
        }),
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: ({ theme }) => ({
          marginTop: 6,
          fontSize: '0.82rem',
          fontWeight: 500,
          lineHeight: 1.45,
          color: theme.palette.text.secondary,
          [theme.breakpoints.down('sm')]: {
            fontSize: '0.85rem',
          },
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 999,
          minHeight: 30,
          backgroundColor: alpha(theme.palette.primary.main, 0.06),
          color: theme.palette.text.primary,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          [theme.breakpoints.down('sm')]: {
            minHeight: 34,
          },
        }),
        label: ({ theme }) => ({
          fontSize: '0.82rem',
          fontWeight: 700,
          lineHeight: 1.3,
          paddingLeft: 10,
          paddingRight: 10,
          [theme.breakpoints.down('sm')]: {
            fontSize: '0.86rem',
          },
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight: 46,
          textTransform: 'none',
          fontSize: '0.93rem',
          fontWeight: 600,
          lineHeight: 1.3,
          color: theme.palette.text.secondary,
          [theme.breakpoints.down('sm')]: {
            minHeight: 48,
            fontSize: '0.96rem',
            paddingLeft: 12,
            paddingRight: 12,
          },
          '&.Mui-selected': {
            color: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: ({ theme }) => ({
          color: theme.palette.text.primary,
          fontSize: '0.98rem',
          fontWeight: 600,
          lineHeight: 1.45,
          [theme.breakpoints.down('sm')]: {
            fontSize: '1rem',
          },
        }),
        secondary: ({ theme }) => ({
          color: theme.palette.text.secondary,
          fontSize: '0.88rem',
          lineHeight: 1.5,
          [theme.breakpoints.down('sm')]: {
            fontSize: '0.92rem',
          },
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderColor: theme.palette.divider,
          color: theme.palette.text.primary,
          paddingTop: 14,
          paddingBottom: 14,
          [theme.breakpoints.down('sm')]: {
            paddingTop: 12,
            paddingBottom: 12,
            paddingLeft: 10,
            paddingRight: 10,
          },
        }),
        head: ({ theme }) => ({
          fontSize: '0.82rem',
          fontWeight: 700,
          letterSpacing: '0.04em',
          lineHeight: 1.4,
          color: theme.palette.text.secondary,
          backgroundColor: alpha(theme.palette.background.default, 0.75),
          [theme.breakpoints.down('sm')]: {
            fontSize: '0.86rem',
          },
        }),
        body: ({ theme }) => ({
          fontSize: '0.95rem',
          lineHeight: 1.5,
          [theme.breakpoints.down('sm')]: {
            fontSize: '0.97rem',
          },
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundImage: 'none',
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${alpha(theme.palette.text.primary, 0.05)}`,
          boxShadow: '0 4px 24px rgba(0,0,0,0.03)',
          borderRadius: 12,
        }),
      },
    },
  },
});

export default theme;
